import { useEffect, useRef } from "react";

const hash = (a, b) => {
  const x = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return x - Math.floor(x);
};

const readVar = (el, name, fallback) => getComputedStyle(el).getPropertyValue(name).trim() || fallback;

// Dot mask of the station name for a cols x rows grid.
const textMask = (text, font, cols, rows, scale = 1, pos = 0.36, solid = false) => {
  const c = document.createElement("canvas");
  c.width = cols; c.height = rows;
  const g = c.getContext("2d");
  const words = text.toUpperCase().split(/\s+/).filter(Boolean);
  const fit = (lines, maxH) => {
    g.font = `${maxH}px "${font}"`;
    const widest = Math.max(...lines.map((l) => g.measureText(l).width));
    return Math.min(maxH, (maxH * cols * 0.9) / widest);
  };
  let lines = [words.join(" ")];
  let size = fit(lines, rows * 0.42 * scale);
  if (size < rows * 0.24 * scale && words.length > 1) {
    const mid = Math.ceil(words.length / 2);
    lines = [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
    size = fit(lines, rows * 0.27 * scale);
  }
  g.fillStyle = "#fff";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.font = `${size}px "${font}"`;
  g.font = `${size}px "${font}"`;
  g.textBaseline = "middle";
  const ascent = Math.max(...lines.map((l) => g.measureText(l).actualBoundingBoxAscent || size * 0.6)) + size * 0.1;
  const block = lines.length * size * 1.05;
  const top = Math.min(Math.max(rows * pos - ((lines.length - 1) * size * 1.05) / 2, ascent), rows - block + size * 0.4);
  lines.forEach((l, i) => g.fillText(l, cols / 2, top + i * size * 1.05));
  if (solid) return { lines, size, top };
  const data = g.getImageData(0, 0, cols, rows).data;
  const cells = [];
  for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) if (data[(y * cols + x) * 4 + 3] > 110) cells.push([x, y]);
  return cells;
};

const dotPath = (cells, pitch, r) => {
  const p = new Path2D();
  cells.forEach(([x, y]) => { p.moveTo(x * pitch + pitch / 2 + r, y * pitch + pitch / 2); p.arc(x * pitch + pitch / 2, y * pitch + pitch / 2, r, 0, 7); });
  return p;
};

export const levels = (n, analyser, freq, pulse, beat, energy, sens) => {
  const out = new Array(n);
  for (let i = 0; i < n; i++) {
    const bass = 1 - i / n;
    let v;
    if (analyser) {
      const bin = Math.floor(Math.pow(i / n, 1.6) * freq.length * 0.7);
      v = (freq[bin] / 255) * 0.85 + pulse * 0.15 * bass;
    } else {
      v = (0.25 + 0.6 * hash(i, beat) * (0.5 + 0.5 * bass)) * (0.45 + 0.55 * pulse * (0.4 + 0.6 * bass)) * energy;
    }
    out[i] = Math.min(1, v * sens);
  }
  return out;
};

export const LedVisualizer = ({ clock, audioApi, text, font, playing, opts, imageUrl, className = "w-full h-[260px] sm:h-[380px] lg:h-[440px]" }) => {
  const wrap = useRef(null);
  const canvas = useRef(null);
  const live = useRef({});
  live.current = { audioApi, playing, opts };
  const { pitch: pitchOpt = 8, showName = true, nameStyle = "dots", nameSize = 100, namePos = 36 } = opts || {};

  useEffect(() => {
    const el = wrap.current;
    const cv = canvas.current;
    const ctx = cv.getContext("2d");
    let raf, cols, rows, pitch, dpr, w, h, bg, textPath, solidText, logo, bgColor, heights = [], sparkles = [], lastBeat = -1, freq, wave;
    let stopped = false;

    const build = async () => {
      try { await document.fonts.load(`40px "${font}"`); } catch {}
      if (stopped) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = el.clientWidth; h = el.clientHeight;
      pitch = Math.max(4, Math.round(pitchOpt * (w < 640 ? 0.75 : 1)));
      cols = Math.floor(w / pitch); rows = Math.floor(h / pitch);
      cv.width = w * dpr; cv.height = h * dpr;
      cv.style.width = `${w}px`; cv.style.height = `${h}px`;
      bgColor = readVar(el, "--t-viz", "#ffe600");
      bg = document.createElement("canvas");
      bg.width = cv.width; bg.height = cv.height;
      const b = bg.getContext("2d");
      b.scale(dpr, dpr);
      b.fillStyle = bgColor; b.globalAlpha = 0.08;
      const all = [];
      for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) all.push([x, y]);
      b.fill(dotPath(all, pitch, Math.max(1, pitch * 0.15)));
      const label = text || "Lowride Life Radio";
      const useImage = nameStyle === "image" && imageUrl;
      const solid = nameStyle === "solid" || (nameStyle === "image" && !imageUrl);
      logo = null;
      if (showName && useImage) {
        const img = new Image();
        img.onerror = () => { if (!stopped) { solidText = textMask(label, font, cols, rows, nameSize / 100, namePos / 100, true); } };
        img.onload = () => {
          if (stopped) return;
          const maxH = h * 0.5 * (nameSize / 100), maxW = w * 0.9;
          const k = Math.min(maxH / img.height, maxW / img.width);
          const lw = img.width * k, lh = img.height * k;
          const cy = Math.min(Math.max(h * (namePos / 100), lh / 2 + 4), h - lh / 2 - 4);
          logo = { img, x: (w - lw) / 2, y: cy - lh / 2, lw, lh };
        };
        img.src = imageUrl;
      }
      textPath = showName && !solid && !useImage ? dotPath(textMask(label, font, cols, rows, nameSize / 100, namePos / 100), pitch, pitch * 0.36) : null;
      solidText = showName && solid ? textMask(label, font, cols, rows, nameSize / 100, namePos / 100, true) : null;
      heights = new Array(cols).fill(0);
    };

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      if (!bg) return;
      const { audioApi: api, playing: on, opts: o = {} } = live.current;
      const style = o.style || "led";
      const analyser = on ? api?.analyser?.() : null;
      if (api?.latency) clock.latency = on ? api.latency() : 0;
      if (analyser) {
        if (!freq || freq.length !== analyser.frequencyBinCount) { freq = new Uint8Array(analyser.frequencyBinCount); wave = new Uint8Array(analyser.fftSize); }
        analyser.getByteFrequencyData(freq);
        clock.feed((freq[1] + freq[2] + freq[3] + freq[4]) / 4, now);
      }
      const { pulse, beat } = clock.pulse(now);
      const color = readVar(el, "--t-viz", "#ffe600");
      const peak = readVar(el, "--t-viz2", "#ffb800");
      const nameColor = readVar(el, "--t-name", color);
      if (color !== bgColor) { bgColor = color; build(); return; }
      const energy = on ? 1 : 0.7;
      const sens = (o.sensitivity ?? 100) / 100;
      const maxH = rows * ((o.height ?? 36) / 100);
      const glow = (o.glow ?? 30) / 100;
      const mirror = o.mirror ?? true;
      const n = mirror ? Math.ceil(cols / 2) : cols;
      const lv = levels(n, analyser, freq, pulse, beat, energy, sens);
      for (let i = 0; i < n; i++) {
        const t = lv[i] * maxH;
        heights[i] = t > heights[i] ? t : heights[i] * 0.9 + t * 0.1;
      }
      if (beat !== lastBeat) {
        lastBeat = beat;
        if (o.sparkles ?? true) for (let k = 0; k < 2; k++) sparkles.push({ x: Math.floor(Math.random() * cols), y: Math.floor(Math.random() * rows * 0.6), born: now });
      }
      sparkles = sparkles.filter((s) => now - s.born < 700);

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, cv.width, cv.height);
      if (style === "led") ctx.drawImage(bg, 0, 0);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.shadowColor = color;
      ctx.shadowBlur = glow * 18;

      if (textPath) {
        ctx.fillStyle = nameColor;
        ctx.shadowColor = nameColor;
        ctx.globalAlpha = 0.55 + 0.45 * pulse * energy;
        ctx.fill(textPath);
        ctx.shadowColor = color;
      }
      if (logo) {
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
        ctx.drawImage(logo.img, logo.x, logo.y, logo.lw, logo.lh);
        ctx.shadowBlur = glow * 18;
        ctx.shadowColor = color;
      }
      if (solidText) {
        ctx.fillStyle = nameColor;
        ctx.shadowColor = nameColor;
        ctx.globalAlpha = 0.8 + 0.2 * pulse * energy;
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.font = `${solidText.size * pitch}px "${font}"`;
        solidText.lines.forEach((l, i) => ctx.fillText(l, w / 2, (solidText.top + i * solidText.size * 1.05) * pitch));
        ctx.shadowColor = color;
      }

      const xs = (i) => (mirror ? [Math.ceil(cols / 2) - 1 - i, cols - Math.ceil(cols / 2) + i] : [i]);
      const r = pitch * 0.34;
      if (style === "led") {
        const bars = [], tops = [];
        for (let i = 0; i < n; i++) {
          const m = Math.round(heights[i]);
          for (const x of xs(i)) for (let j = 0; j < m; j++) (j === m - 1 ? tops : bars).push([x, rows - 1 - j]);
        }
        ctx.fillStyle = color; ctx.globalAlpha = 0.85; ctx.fill(dotPath(bars, pitch, r));
        ctx.fillStyle = peak; ctx.globalAlpha = 1; ctx.fill(dotPath(tops, pitch, r));
      } else if (style === "bars") {
        const grad = ctx.createLinearGradient(0, h, 0, h - maxH * pitch);
        grad.addColorStop(0, color); grad.addColorStop(1, peak);
        ctx.fillStyle = grad; ctx.globalAlpha = 0.95;
        const bw = pitch * 2;
        for (let i = 0; i < n; i += 2) {
          const bh = heights[i] * pitch;
          for (const x of xs(i)) {
            const px = mirror && x < cols / 2 ? x * pitch - pitch : x * pitch;
            ctx.beginPath();
            ctx.roundRect ? ctx.roundRect(px + 1, h - bh, bw - 2, bh, 3) : ctx.rect(px + 1, h - bh, bw - 2, bh);
            ctx.fill();
          }
        }
      } else {
        const mid = h - maxH * pitch * 0.55;
        const amp = maxH * pitch * 0.5;
        const grad = ctx.createLinearGradient(0, 0, w, 0);
        grad.addColorStop(0, color); grad.addColorStop(0.5, peak); grad.addColorStop(1, color);
        ctx.strokeStyle = grad; ctx.lineWidth = Math.max(2, pitch * 0.4); ctx.globalAlpha = 1;
        ctx.beginPath();
        if (analyser) analyser.getByteTimeDomainData(wave);
        const pts = 160;
        for (let k = 0; k <= pts; k++) {
          const x = (k / pts) * w;
          let v;
          if (analyser) v = (wave[Math.floor((k / pts) * (wave.length - 1))] - 128) / 128 * sens * 1.6;
          else v = (Math.sin(k * 0.21 + now / 300) * 0.5 + Math.sin(k * 0.07 - now / 700) * 0.5) * (0.25 + 0.75 * pulse) * energy * sens;
          const y = mid - Math.max(-1, Math.min(1, v)) * amp;
          k ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
        }
        ctx.stroke();
        ctx.globalAlpha = 0.25; ctx.lineWidth = Math.max(1, pitch * 0.15); ctx.strokeStyle = peak;
        ctx.beginPath(); ctx.moveTo(0, mid); ctx.lineTo(w, mid); ctx.stroke();
      }

      ctx.fillStyle = peak;
      sparkles.forEach((s) => {
        ctx.globalAlpha = 1 - (now - s.born) / 700;
        const cells = [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]].map(([dx, dy]) => [s.x + dx, s.y + dy]);
        ctx.fill(dotPath(cells, pitch, r));
      });
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
    };

    build();
    raf = requestAnimationFrame(frame);
    const ro = new ResizeObserver(() => build());
    ro.observe(el);
    return () => { stopped = true; cancelAnimationFrame(raf); ro.disconnect(); };
  }, [clock, text, font, pitchOpt, showName, nameStyle, nameSize, namePos, imageUrl]);

  return (
    <div ref={wrap} className={className} data-testid="led-visualizer">
      <canvas ref={canvas} aria-label="Beat-synced visualizer" />
    </div>
  );
};
